import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { donationService } from '../services/firebaseService';
import { DonationContent, DonationOption } from '../types';
import { functions } from '../firebase/firebaseConfig';
import { httpsCallable } from 'firebase/functions';

// Payment option display config
const getPaymentOptionConfig = (type: DonationOption['type']) => {
  const configs: Record<string, { icon: string; iconColor: string }> = {
    stripe: { icon: '💳', iconColor: '#635BFF' },
    paypal: { icon: 'P', iconColor: '#0070BA' },
    venmo: { icon: '$', iconColor: '#3D95CE' },
    zelle: { icon: '$', iconColor: '#6C1D45' },
    buymeacoffee: { icon: '☕', iconColor: '#FFDD00' },
    kofi: { icon: '☕', iconColor: '#FF5E5B' }
  };
  return configs[type] || { icon: '💳', iconColor: '#666666' };
};

// Supported currencies
const SUPPORTED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', stripeCode: 'usd', minAmount: 0.50 },
  { code: 'EUR', symbol: '€', name: 'Euro', stripeCode: 'eur', minAmount: 0.50 },
  { code: 'GBP', symbol: '£', name: 'British Pound', stripeCode: 'gbp', minAmount: 0.30 },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', stripeCode: 'cad', minAmount: 0.50 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', stripeCode: 'aud', minAmount: 0.50 },
];

const DonatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [donationContent, setDonationContent] = useState<DonationContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  
  // Get current currency config
  const currentCurrency = SUPPORTED_CURRENCIES.find(c => c.code === selectedCurrency) || SUPPORTED_CURRENCIES[0];
  
  // Preset donation amounts
  const presetAmounts = [25, 50, 100, 250, 500];

  useEffect(() => {
    loadDonationContent();
    
    // Check for success/cancel from Stripe redirect
    const successParam = searchParams.get('success');
    const canceledParam = searchParams.get('canceled');
    
    if (successParam === 'true') {
      setSuccess(true);
      // Clear the success param from URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (canceledParam === 'true') {
      setError('Payment was canceled. You can try again anytime.');
      // Clear the canceled param from URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  const loadDonationContent = async () => {
    try {
      const content = await donationService.getDonationContent();
      setDonationContent(content);
    } catch (error) {
      console.error('Error loading donation content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStripeDonate = async (amount: number) => {
    setIsProcessing(true);
    setError(null);

    try {
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({ 
        amount,
        currency: currentCurrency.stripeCode
      });
      
      const data = result.data as { url: string; sessionId: string };
      
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      // Better error message handling
      let errorMessage = 'Failed to start payment. Please try again.';
      if (err.code === 'functions/unavailable' || err.code === 'unavailable') {
        errorMessage = 'Payment service is temporarily unavailable. Please try again in a moment.';
      } else if (err.code === 'functions/permission-denied' || err.code === 'permission-denied') {
        errorMessage = 'Payment service is not configured. Please contact support.';
      } else if (err.message && !err.message.includes('internal')) {
        errorMessage = err.message;
      } else if (err.code) {
        errorMessage = `Payment error: ${err.code}. Please try again or contact support.`;
      }
      setError(errorMessage);
      setIsProcessing(false);
    }
  };

  const renderDonationOption = (option: DonationOption) => {
    if (!option.enabled) return null;

    // Only show options that have payment URLs configured (or zelle with email/phone, or stripe)
    const hasPaymentInfo = option.paymentUrl?.trim() || 
                          (option.type === 'zelle' && (option.zelleEmail || option.zellePhone)) ||
                          option.type === 'stripe';
    if (!hasPaymentInfo) return null;

    const config = getPaymentOptionConfig(option.type);
    const isComingSoon = !option.paymentUrl && option.type !== 'zelle';

    // Special layout for Stripe: logo on left, form on right
    if (option.type === 'stripe') {
      return (
        <div 
          key={option.id}
          style={{
            gridColumn: '1 / -1',
            display: 'flex',
            gap: '2rem',
            alignItems: 'flex-start',
            padding: '2rem',
            border: `2px solid ${config.iconColor}`,
            borderRadius: '12px',
            background: '#ffffff',
            maxWidth: '800px',
            margin: '0 auto'
          }}
        >
          {/* Logo on the left */}
          <div style={{
            flex: '0 0 200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img 
              src="/Logo_TM.png" 
              alt="Compassion Course Foundation Logo"
              style={{
                maxWidth: '100%',
                height: 'auto',
                marginBottom: '1rem'
              }}
            />
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1em', textAlign: 'center' }}>{option.name}</h3>
            {option.description && (
              <p style={{ fontSize: '0.9em', color: '#666', margin: 0, textAlign: 'center' }}>
                {option.description}
              </p>
            )}
          </div>

          {/* Form on the right - half width */}
          <div style={{ flex: '1', minWidth: 0 }}>
            {/* Currency and Custom Amount - Side by Side */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '1rem', 
              marginBottom: '1rem' 
            }}>
              {/* Currency Selection */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  fontSize: '0.9em',
                  color: 'var(--text-dark)'
                }}>
                  Currency
                </label>
                <select
                  value={selectedCurrency}
                  onChange={(e) => {
                    setSelectedCurrency(e.target.value);
                    setSelectedAmount(null);
                    setCustomAmount('');
                  }}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontSize: '0.9em',
                    transition: 'all 0.2s ease',
                    fontFamily: 'var(--font-primary)',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = config.iconColor;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${config.iconColor}20`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {SUPPORTED_CURRENCIES.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} {currency.code}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Custom Amount */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  fontSize: '0.9em',
                  color: 'var(--text-dark)'
                }}>
                  Custom Amount
                </label>
                <input
                  type="number"
                  min="0.50"
                  step="0.01"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedAmount(null);
                  }}
                  placeholder="0.00"
                  min={currentCurrency.minAmount}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontSize: '0.9em',
                    transition: 'all 0.2s ease',
                    fontFamily: 'var(--font-primary)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = config.iconColor;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${config.iconColor}20`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
            
            {/* Preset Amounts - Horizontal */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                fontSize: '0.9em',
                color: 'var(--text-dark)'
              }}>
                Quick Select
              </label>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className="btn"
                    style={{
                      padding: '0.625rem 1rem',
                      border: selectedAmount === amount ? `2px solid ${config.iconColor}` : '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      background: selectedAmount === amount ? config.iconColor : 'white',
                      color: selectedAmount === amount ? 'white' : 'var(--text-dark)',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      fontWeight: selectedAmount === amount ? '600' : '500',
                      transition: 'all 0.2s ease',
                      boxShadow: selectedAmount === amount ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none',
                      flex: '0 1 auto'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedAmount !== amount) {
                        e.currentTarget.style.borderColor = config.iconColor;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedAmount !== amount) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {currentCurrency.symbol}{amount}
                  </button>
                ))}
              </div>
              <p style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-light)', 
                marginTop: '0.5rem', 
                marginBottom: 0 
              }}>
                Minimum: {currentCurrency.symbol}{currentCurrency.minAmount}
              </p>
            </div>
            
            {/* Email Receipt Notice */}
            <div style={{
              padding: '1rem',
              backgroundColor: '#E3F2FD',
              border: '2px solid #2196F3',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <span style={{ 
                fontSize: '1.25rem', 
                lineHeight: '1',
                flexShrink: 0
              }}>📧</span>
              <div style={{ flex: 1 }}>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.9em', 
                  fontWeight: '600',
                  color: '#1565C0',
                  marginBottom: '0.25rem'
                }}>
                  Email Receipt Available
                </p>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.85em', 
                  color: '#1976D2',
                  lineHeight: '1.4'
                }}>
                  To receive an email receipt for your donation, please enter your email address when prompted during checkout.
                </p>
              </div>
            </div>
            
            {/* Donate Button */}
            <button
              onClick={() => {
                const amount = selectedAmount || parseFloat(customAmount);
                if (!amount || amount < currentCurrency.minAmount) {
                  setError(`Please select or enter a minimum donation of ${currentCurrency.symbol}${currentCurrency.minAmount}`);
                  return;
                }
                handleStripeDonate(amount);
              }}
              disabled={isProcessing || (!selectedAmount && !customAmount)}
              className="btn btn-primary"
              style={{ 
                background: config.iconColor, 
                color: 'white', 
                border: 'none',
                padding: '1rem 1.5rem',
                borderRadius: '0.5rem',
                fontSize: '1.1em',
                fontWeight: '600',
                cursor: (isProcessing || (!selectedAmount && !customAmount)) ? 'not-allowed' : 'pointer',
                display: 'block',
                width: '100%',
                opacity: (isProcessing || (!selectedAmount && !customAmount)) ? 0.6 : 1,
                transition: 'all 0.3s ease',
                boxShadow: (isProcessing || (!selectedAmount && !customAmount)) ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                if (!isProcessing && (selectedAmount || customAmount)) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
            >
              {isProcessing ? 'Processing...' : `Donate ${currentCurrency.symbol}${selectedAmount || customAmount || '0.00'}`}
            </button>
          </div>
        </div>
      );
    }

    // Regular layout for other payment options
    return (
      <div 
        key={option.id} 
        style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '1.5rem',
          border: `2px solid ${config.iconColor}`,
          borderRadius: '12px',
          background: '#ffffff',
          transition: 'all 0.2s',
          cursor: isComingSoon ? 'default' : 'pointer',
          textAlign: 'center'
        }}
        onMouseEnter={(e) => {
          if (!isComingSoon) {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = `0 8px 16px rgba(0,0,0,0.1)`;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '12px',
            background: config.iconColor,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            fontWeight: 'bold',
            marginBottom: '1rem'
          }}
        >
          {config.icon}
        </div>
        
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1em' }}>{option.name}</h3>
        {option.description && (
          <p style={{ fontSize: '0.9em', color: '#666', margin: '0 0 1rem 0' }}>
            {option.description}
          </p>
        )}
        {option.type === 'zelle' && (
          <>
            <p style={{ fontSize: '0.9em', margin: '0 0 1rem 0' }}>
              Send to our Zelle {option.zellePhone ? 'phone' : 'email'}:
              <br />
              <strong style={{ fontSize: '1em' }}>{option.zelleEmail || option.zellePhone}</strong>
            </p>
            <button 
              className="btn btn-secondary" 
              style={{ 
                background: config.iconColor, 
                color: 'white', 
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1em',
                fontWeight: '500'
              }}
            >
              View Instructions
            </button>
          </>
        )}

        {(option.type === 'paypal' || option.type === 'venmo' || 
          option.type === 'buymeacoffee' || option.type === 'kofi') && (
          <a
            href={(() => {
              // Handle Venmo usernames that start with @
              if (option.type === 'venmo' && option.paymentUrl?.startsWith('@')) {
                return `https://venmo.com/${option.paymentUrl.substring(1)}`;
              }
              return option.paymentUrl || '#';
            })()}
            className="btn btn-primary"
            target={option.paymentUrl ? '_blank' : undefined}
            rel={option.paymentUrl ? 'noopener noreferrer' : undefined}
            style={{ 
              background: config.iconColor, 
              color: 'white', 
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1em',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'inline-block'
            }}
          >
            {option.buttonText || 'Donate Now'}
          </a>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  const displayContent = donationContent || {
    title: 'Donate',
    description: 'Choose a donation method below. We\'ll wire up processing later.',
    options: []
  };

  return (
    <Layout>
      <div className="container">
        <h1>{displayContent.title || 'Donate'}</h1>
        {displayContent.description && <p>{displayContent.description}</p>}

        {success && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '8px',
            color: '#155724',
            marginTop: '1rem',
            marginBottom: '1rem'
          }}>
            <strong>Thank you!</strong> Your donation was successful. We appreciate your support!
          </div>
        )}

        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            color: '#c33',
            marginTop: '1rem',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        <div style={{ 
          marginTop: '2rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1.5rem',
          maxWidth: '1200px',
          margin: '2rem auto 0'
        }}>
          {displayContent.options && displayContent.options.length > 0 ? (
            displayContent.options
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(renderDonationOption)
          ) : (
            <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#666' }}>
              No donation options configured yet.
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DonatePage;
